import {AntdConfig} from '@react-awesome-query-builder/antd'
import '@react-awesome-query-builder/antd/css/styles.scss'
import {Builder, Utils as QbUtils, Query} from '@react-awesome-query-builder/ui'
import {useMutation} from '@tanstack/react-query'
import bg_BG from 'antd/es/locale/bg_BG'
import en_EN from 'antd/es/locale/en_GB'
import {merge} from 'antd/es/theme/util/statistic'
import throttle from 'lodash/throttle'
import {Button} from 'primereact/button'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {getLanguageCode} from '../../i18n/LanguageUtils'
import TranslateUtils from '../../i18n/TranslateUtils'
import PageContentWrapper from '../../utils/LayoutUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getUserId} from '../authentication/AuthUtils'
import {component} from '../form/Constant'
import {showLoading, stopLoading} from '../loading/LoadingService'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import StyledHospitalOptions from '../styled-component/styled-options/StyledHospitalOptions'
import './ReportBuilderStyle.css'
import {fieldList} from './ReportConstanst'
import {findPatientByQuery} from './ReportEndPoint'
import ReportTableResult from './ReportTableResult'
const queryValue = {id: QbUtils.uuid(), type: 'group'}

const ReportQueryBuilder = () => {
    //#region Ref
    const resultTableRef = useRef(null)
    const multiselectRef = useRef(null)
    const languageRef = useRef(null)
    //#endregion

    //#region Translate
    const {t} = useTranslation()
    const {i18n} = useTranslation()

    const {translateCommons, translateDatatable} = TranslateUtils()

    const translateReport = useCallback(
        key => {
            return key !== t(key, {ns: 'report'}) ? t(key, {ns: 'report'}) : key
        },
        [t],
    )

    const translateReportKeys = useCallback(
        key => {
            return key !== t(key, {ns: 'keys'}) ? t(key, {ns: 'keys'}) : key
        },
        [t],
    )

    const translateOptionsFields = useCallback(
        (arr = []) => {
            let options = []

            if (!!isArrayAndNotEmpty) {
                for (let i = 0; i < arr.length; i++) {
                    let item = {...arr[i]}
                    item.title =
                        arr[i]?.title !== t(arr[i]?.title, {ns: 'options'})
                            ? t(arr[i]?.title, {ns: 'options'})
                            : arr[i]?.title

                    options.push(item)
                }
            }
            return options
        },
        [t],
    )

    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole, hospitalBelongs} = useAppConfig()
    //#endregion

    //#region Memo
    // Initial Config for Query Builder
    const translateConfig = useMemo(() => {
        let conjunctions = {
            ...AntdConfig.conjunctions,
            AND: {
                ...AntdConfig.conjunctions.AND,
                label: translateReport('And'),
            },
            OR: {
                ...AntdConfig.conjunctions.OR,
                label: translateReport('Or'),
            },
        }

        let operators = {
            ...AntdConfig.operators,
            equal: {
                ...AntdConfig.operators.equal,
                label: translateReport('Equal'),
            },
            not_equal: {
                ...AntdConfig.operators.not_equal,
                label: translateReport('Not equal'),
            },
            all: {
                ...AntdConfig.operators.all,
                label: translateReport('All'),
            },
            starts_with: {
                ...AntdConfig.operators.starts_with,
                elasticSearchQueryType: 'regexp',
                label: translateReport('Starts with'),
                labelForFormat: translateReport('Starts with'),
            },
            ends_with: {
                ...AntdConfig.operators.ends_with,
                elasticSearchQueryType: 'regexp',
                label: translateReport('Ends with'),
                labelForFormat: translateReport('Ends with'),
            },
            is_empty: {
                ...AntdConfig.operators.is_empty,
                label: translateReport('Is empty'),
                labelForFormat: translateReport('IS EMPTY'),
            },
            is_not_empty: {
                ...AntdConfig.operators.is_not_empty,
                label: translateReport('Is not empty'),
                labelForFormat: translateReport('IS NOT EMPTY'),
            },
            is_not_null: {
                ...AntdConfig.operators.is_not_null,
                label: translateReport('Is not null'),
                labelForFormat: translateReport('IS NOT NULL'),
            },
            is_null: {
                ...AntdConfig.operators.is_null,
                label: translateReport('Is null'),
                labelForFormat: translateReport('IS NULL'),
            },
            like: {
                ...AntdConfig.operators.like,
                label: translateReport('Contains'),
                labelForFormat: translateReport('Contains'),
            },
            multiselect_contains: {
                ...AntdConfig.operators.multiselect_contains,
                label: translateReport('Contains'),
                labelForFormat: translateReport('CONTAINS'),
            },
            multiselect_not_contains: {
                ...AntdConfig.operators.multiselect_not_contains,
                label: translateReport('Not contains'),
                labelForFormat: translateReport('NOT CONTAINS'),
            },
            multiselect_equals: {
                ...AntdConfig.operators.multiselect_equals,
                label: translateReport('Equals'),
            },
            multiselect_not_equals: {
                ...AntdConfig.operators.multiselect_not_equals,
                label: translateReport('Not equals'),
            },
            none: {
                ...AntdConfig.operators.none,
                label: translateReport('None'),
                labelForFormat: translateReport('NONE'),
            },
            not_between: {
                ...AntdConfig.operators.not_between,
                label: translateReport('Not between'),
                labelForFormat: translateReport('NOT BETWEEN'),
            },
            not_like: {
                ...AntdConfig.operators.not_like,
                label: translateReport('Not contains'),
                labelForFormat: translateReport('Not contains'),
            },
            proximity: {
                ...AntdConfig.operators.proximity,
                label: translateReport('Proximity search'),
            },
            select_any_in: {
                ...AntdConfig.operators.select_any_in,
                label: translateReport('Any in'),
                labelForFormat: translateReport('IN'),
            },
            select_not_any_in: {
                ...AntdConfig.operators.select_not_any_in,
                label: translateReport('Not in'),
                labelForFormat: translateReport('NOT IN'),
            },
            some: {
                ...AntdConfig.operators.some,
                label: translateReport('Some'),
                labelForFormat: translateReport('SOME'),
            },
            between: {
                ...AntdConfig.operators.between,
                label: translateReport('Between'),
                labelForFormat: translateReport('BETWEEN'),
                valueLabels: [
                    translateReport('Value from'),
                    translateReport('Value to'),
                ],
                textSeparators: [
                    translateReport('from'),
                    translateReport('to'),
                ],
            },
        }

        // Overide Some Elastic Search Query (Some elastic search operators in lib is not work)
        let elasticSearchFormatValueForText = (esQueryType, val, op, field) => {
            if (!field) {
                return {}
            }

            if (
                op === 'is_not_empty' ||
                op === 'is_empty' ||
                op === 'is_not_null' ||
                op === 'is_null'
            ) {
                return {field: field}
            }

            if (!val || val?.length < 1 || !val[0]) {
                return {[field]: ''}
            }

            if (esQueryType === 'regexp') {
                if (op === 'like') {
                    if (val && val?.length > 0 && !!val[0]) {
                        let stringVal = '.*' + val[0] + '.*'
                        return {[field]: stringVal}
                    }
                } else if (op === 'starts_with') {
                    if (val && val?.length > 0 && !!val[0]) {
                        let stringVal = val[0] + '.*'
                        return {[field]: stringVal}
                    }
                } else if (op === 'ends_with') {
                    if (val && val?.length > 0 && !!val[0]) {
                        let stringVal = '.*' + val[0]
                        return {[field]: stringVal}
                    }
                }
            }

            return {[field]: val[0]}
        }

        let widgets = {
            ...AntdConfig.widgets,
            // examples of overriding
            number: {
                ...AntdConfig.widgets.number,
                valueLabel: translateReport('Number'),
            },
            boolean: {
                ...AntdConfig.widgets.boolean,
                labelNo: translateReport('No'),
                labelYes: translateReport('Yes'),
            },
            text: {
                ...AntdConfig.widgets.text,
                valueLabel: translateReport('String'),
                valuePlaceholder: translateReport('Enter string'),
                elasticSearchFormatValue: elasticSearchFormatValueForText,
            },
            textarea: {
                ...AntdConfig.widgets.textarea,
                maxRows: 3,
                valueLabel: translateReport('Text'),
                valuePlaceholder: translateReport('Enter text'),
            },
            slider: {
                ...AntdConfig.widgets.slider,
                valueLabel: translateReport('Number'),
                valuePlaceholder: translateReport(
                    'Enter number or move slider',
                ),
            },
            rangeslider: {
                ...AntdConfig.widgets.rangeslider,
                valueLabel: translateReport('Range'),
                valueLabels: [
                    {
                        label: translateReport('Number from'),
                        placeholder: translateReport('Enter number from'),
                    },
                    {
                        label: translateReport('Number to'),
                        placeholder: translateReport('Enter number to'),
                    },
                ],
            },
            date: {
                ...AntdConfig.widgets.date,
                dateFormat: 'DD.MM.YYYY',
                valueFormat: 'YYYY-MM-DD',
                valuePlaceholder: translateReport('Enter date'),
                valueLabels: [
                    {
                        label: translateReport('Date from'),
                        placeholder: translateReport('Enter date from'),
                    },
                    {
                        label: translateReport('Date to'),
                        placeholder: translateReport('Enter date to'),
                    },
                ],
            },
            time: {
                ...AntdConfig.widgets.time,
                timeFormat: 'HH:mm',
                valueFormat: 'HH:mm:ss',

                valueLabel: translateReport('Time'),
                valueLabels: [
                    {
                        label: translateReport('Time from'),
                        placeholder: translateReport('Enter time from'),
                    },
                    {
                        label: translateReport('Time to'),
                        placeholder: translateReport('Enter time to'),
                    },
                ],

                valuePlaceholder: translateReport('Enter time'),
            },
            datetime: {
                ...AntdConfig.widgets.datetime,
                timeFormat: 'HH:mm',
                dateFormat: 'DD.MM.YYYY',
                valueFormat: 'YYYY-MM-DD HH:mm:ss',
                valueLabel: translateReport('Datetime'),
                valuePlaceholder: translateReport('Enter datetime'),
            },
            func: {
                ...AntdConfig.widgets.func,
                customProps: {
                    showSearch: true,
                },
                valueLabel: translateReport('Function'),
                valuePlaceholder: translateReport('Select function'),
            },
            select: {
                ...AntdConfig.widgets.select,
                valueLabel: translateReport('Value'),
                valuePlaceholder: translateReport('Select value'),
            },
            multiselect: {
                ...AntdConfig.widgets.multiselect,
                valueLabel: translateReport('Values'),
                valuePlaceholder: translateReport('Select values'),
                // elasticSearchFormatValue: elasticSearchFormatValueForSelect,
            },
            treeselect: {
                ...AntdConfig.widgets.treeselect,
                customProps: {
                    showSearch: true,
                },
                valueLabel: translateReport('Value'),
                valuePlaceholder: translateReport('Select value'),
            },
        }

        let types = {
            ...AntdConfig.types,
            // examples of  overriding
            text: {
                ...AntdConfig.types.text,
                excludeOperators: ['proximity'],
            },
            boolean: merge({}, AntdConfig.types.boolean, {
                boolean: {
                    widgetProps: {
                        hideOperator: true,
                        operatorInlineLabel: translateReport('is'),
                    },
                    opProps: {
                        equal: {
                            label: translateReport('is'),
                        },
                        not_equal: {
                            label: translateReport('is not'),
                        },
                    },
                },
            }),
        }

        let settings = {
            ...AntdConfig.settings,
            removeIncompleteRulesOnLoad: true,
            removeEmptyRulesOnLoad: true,
            removeEmptyGroupsOnLoad: true,
            removeInvalidMultiSelectValuesOnLoad: true,

            locale: {
                moment: getLanguageCode() === 'bg' ? 'bg' : 'en',
                antd: getLanguageCode() === 'bg' ? bg_BG : en_EN,
            },
            valueLabel: translateReport('Value'),
            valuePlaceholder: translateReport('Value'),
            fieldLabel: translateReport('Field'),
            operatorLabel: translateReport('Operator'),
            funcLabel: translateReport('Function'),
            fieldPlaceholder: translateReport('Select field'),
            funcPlaceholder: translateReport('Select function'),
            operatorPlaceholder: translateReport('Select operator'),
            lockLabel: translateReport('Lock'),
            lockedLabel: translateReport('Locked'),
            deleteLabel: translateReport('Delete'),
            addGroupLabel: translateReport('Add group'),
            addRuleLabel: translateReport('Add rule'),
            addSubRuleLabel: translateReport('Add sub rule'),
            delGroupLabel: translateReport('Delete group'),
            notLabel: translateReport('Not'),
            fieldSourcesPopupTitle: translateReport('Select source'),
            valueSourcesPopupTitle: translateReport('Select value source'),
            removeRuleConfirmOptions: {
                title: translateReport('Are you sure delete this rule?'),
                okText: translateReport('Yes'),
                okType: 'danger',
                cancelText: translateReport('Cancel'),
            },
            removeGroupConfirmOptions: {
                title: translateReport('Are you sure delete this group?'),
                okText: translateReport('Yes'),
                okType: 'danger',
                cancelText: translateReport('Cancel'),
            },
            addCaseLabel: translateReport('Add condition'),
            addDefaultCaseLabel: translateReport('Add default condition'),
            defaultCaseLabel: translateReport('Default:'),

            valueSourcesInfo: {
                value: {
                    label: translateReport('Value'),
                },
                field: {
                    label: translateReport('Field'),
                    widget: 'field',
                },
                func: {
                    label: translateReport('Function'),
                    widget: 'func',
                },
            },
        }

        let funcs = {}
        let ctx = AntdConfig.ctx

        return {
            ctx,
            conjunctions,
            operators,
            widgets,
            types,
            settings,
            funcs,
        }
    }, [translateReport])

    // Translate Options and Sub Group query
    const fieldListTranslate = useMemo(() => {
        let fieldListHolder = {}

        const fieldListTemp = {...fieldList}
        Object.keys(fieldList).forEach(item => {
            if (item === 'pathways' || item === 'health_records') {
                let subfields = {}

                Object.keys(fieldListTemp[item]?.subfields).forEach(
                    childItem => {
                        subfields[childItem] = {
                            ...fieldListTemp[item]?.subfields[childItem],
                            label: translateReportKeys(
                                fieldListTemp[item]?.subfields[childItem].label,
                            ),
                        }
                    },
                )

                fieldListHolder[item] = {
                    ...fieldListTemp[item],
                    label: translateReportKeys(fieldListTemp[item].label),
                    subfields: subfields,
                }
            } else if (
                item === 'gender.keyword' ||
                item === 'title.keyword' ||
                item === 'accommodation_status.keyword' ||
                item === 'religion.keyword' ||
                item === 'marital_status.keyword' ||
                item === 'diagnostic.keyword' ||
                item === 'identity_with_held_reason.keyword' ||
                item === 'blood_pressure_limb.keyword' ||
                item === 'blood_pressure_position.keyword' ||
                item === 'level_of_consciousness.keyword'
            ) {
                fieldListHolder[item] = {
                    ...fieldListTemp[item],
                    label: translateReportKeys(fieldListTemp[item].label),
                    fieldSettings: {
                        listValues: translateOptionsFields(
                            fieldListTemp[item].fieldSettings.listValues,
                        ),
                    },
                }
            } else {
                fieldListHolder[item] = {
                    ...fieldListTemp[item],
                    label: translateReportKeys(fieldListTemp[item].label),
                }
            }
        })
        return fieldListHolder
    }, [translateOptionsFields, translateReportKeys])

    const hospitalsBelongOption = useMemo(() => {
        if (hospitalBelongs && hospitalBelongs?.length > 0) {
            return hospitalBelongs?.map(hospital => {
                return {value: hospital.id, label: hospital.name}
            })
        }
    }, [hospitalBelongs])
    //#endregion

    //#region State
    const [state, setState] = useState({
        config: translateConfig,
        tree: QbUtils.loadTree(queryValue),
    })
    const [queryData, setQueryData] = useState(null)
    //#endregion

    //#region Effect
    useEffect(() => {
        if (!!i18n.language && i18n.language !== languageRef.current) {
            languageRef.current = i18n.language
            setState({...state, config: translateConfig})
        }
    }, [i18n.language, state, translateConfig])
    //#endregion

    //#region Render
    // const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const renderBuilder = useCallback(
        props => (
            <div className="query-builder-container">
                <div className="query-builder qb-lite">
                    <div>
                        <div className="mb-2 ">
                            <h4 className="font-bold m-0">
                                {translateCommons('Hospitals')}
                            </h4>
                            <p>
                                {translateDatatable(
                                    'Select the hospitals in which you want to find patients.',
                                )}
                            </p>
                        </div>
                        <StyledHospitalOptions
                            ref={multiselectRef}
                            optionType={component.multiSelect}
                            groupByIsNull={null}
                            placeholder={'Please select hospital'}
                            className="h-3rem"
                            value={
                                userRole === SystemRole.USER_WITH_ROLE &&
                                !!currentHospital
                                    ? [currentHospital?.id]
                                    : []
                            }
                        />
                        <hr />
                    </div>

                    <Builder {...props} />
                </div>
            </div>
        ),
        [hospitalsBelongOption, translateCommons, translateDatatable, userRole],
    )
    //#endregion

    //#region Handle Query
    const handleOnQueryPatient = () => {
        const hospitalValue = multiselectRef?.current?.getValue()

        if (
            (!hospitalValue || hospitalValue?.length < 1) &&
            userRole === SystemRole.ADMIN
        ) {
            showNotification(
                severityNotification.info,
                'Please select your hospitals to query !',
            )
            return
        }

        const toPostData = {
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            queryString: JSON.stringify(
                QbUtils.elasticSearchFormat(state.tree, state.config),
            ),
            hospitalIds: hospitalValue,
        }

        if (!toPostData) {
            showNotification(
                severityNotification.info,
                'Please set up your rule to query !',
            )
            return
        }

        showLoading(`${translateCommons('Finding Patients')}...`)
        handleOnMutateQuery(toPostData)
    }

    const {mutate: handleOnMutateQuery} = useMutation(
        toPostData =>
            findPatientByQuery(getUserId(), toPostData).finally(() =>
                stopLoading(),
            ),
        {
            onSuccess: data => {
                if (!!data) {
                    setQueryData(data)
                    showNotification(
                        severityNotification.success,
                        'Find Patients successfully !!',
                    )
                } else {
                    showNotification(
                        severityNotification.info,
                        'Information',
                        'No patients were found !!',
                    )
                }
                stopLoading()
            },
        },
    )

    const updateResult = throttle((immutableTree, config) => {
        setState(prevState => ({
            ...prevState,
            tree: immutableTree,
            config: config,
        }))
    }, 100)

    const onChange = useCallback(
        (immutableTree, config, actionMeta, actions) => {
            updateResult(immutableTree, config)
        },
        [updateResult],
    )
    //#endregion

    return (
        <PageContentWrapper>
            <div className="flex flex-column gap-4 query-builder-wrapper">
                <div className="col-12 m-0 p-0">
                    <h3 className="pt-2 mb-0 text-darkcyan font-bold text-3xl md:text-4xl ">
                        {translateDatatable('Report Management')}
                    </h3>
                    <i className="underline text-700 text-md font-normal ">
                        {translateDatatable(
                            'You can build queries to find dynamic data! Start by creating your query right here.',
                        )}
                    </i>
                    <hr />
                </div>
                <div className="flex flex-column gap-3 w-full">
                    <Query
                        {...state.config}
                        fields={fieldListTranslate}
                        value={state.tree}
                        onChange={onChange}
                        onInit={onChange}
                        renderBuilder={renderBuilder}
                    />

                    <Button
                        disabled={
                            !QbUtils.queryString(
                                state?.tree,
                                state?.config,
                                true,
                            )
                        }
                        className="w-full"
                        style={{
                            height: '3.5rem',
                            fontSize: '1.2rem',
                        }}
                        label={translateDatatable('SEARCH')}
                        icon="pi pi-search"
                        onClick={handleOnQueryPatient}
                    />
                </div>
                <hr />
                <div>
                    <h5>{translateDatatable('Your query builder is:')} </h5>
                    <div className="col-12 surface-50 border-round p-3 mb-2">
                        <p className="font-bold text-700">
                            {QbUtils.queryString(
                                state?.tree,
                                state?.config,
                                true,
                            )?.length > 0
                                ? JSON.stringify(
                                      QbUtils.queryString(
                                          state?.tree,
                                          state?.config,
                                          true,
                                      ),
                                  )
                                : "Seem like you haven't made any query yet !"}
                        </p>
                    </div>
                </div>
                <div>
                    <h5>{translateDatatable('Results')}: </h5>
                    <div className="col-12 surface-50 border-round p-3 mb-2">
                        {queryData && queryData.length > 0 ? (
                            <div className="flex align-items-center justify-content-between">
                                <h5 className="m-0">
                                    {translateDatatable('There are')}
                                    {queryData.length}{' '}
                                    {translateDatatable(
                                        'results matching the selection.',
                                    )}
                                </h5>
                                <Button
                                    label={translateDatatable('View Result')}
                                    icon="pi pi-eye"
                                    onClick={() =>
                                        resultTableRef.current.showForm(
                                            queryData,
                                        )
                                    }
                                />
                            </div>
                        ) : (
                            <div className="flex align-items-center">
                                <p className="font-bold text-700">
                                    {translateDatatable(
                                        'There are no results matching the selection',
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Report Table Result */}
            <ReportTableResult ref={resultTableRef} />
        </PageContentWrapper>
    )
}
export default ReportQueryBuilder
